import type { TagCatalogRow, TagDefinition } from "../api/generated/model";
import { dayInZone, isTimeZone } from "../format/dates";
import { contains } from "./searchText";

// Contratos y reglas puras de Contactos, iguales a ContactContracts.cs, ContactViewState.cs, ContactPresentation.cs y
// ContactText.cs del portal actual: los mismos nombres de campo que devuelve el servidor y la misma dirección.

export type ContactRights = {
  canRead: boolean;
  canWrite: boolean;
  canReadAccounts: boolean;
  canWriteAccounts: boolean;
  moduleEnabled: boolean;
  globalScope: boolean;
  canImport: boolean;
  canExport: boolean;
  canOpenChat: boolean;
  canReadTags: boolean;
  canManageTags: boolean;
};
export type ContactPerson = {
  id: number;
  firstName: string;
  lastName: string;
  createdAt: string;
  updatedAt: string | null;
  rowVersion: number;
  email: string | null;
  phone: string | null;
  relationshipStatus: string | null;
  tags: string[] | null;
  emailVerified: boolean;
  phoneVerified: boolean;
};
export type ContactSession = {
  channelContactId: number;
  sessionId: number | null;
  conversationId: number | null;
  lastVisibleConversationActivityAt: string | null;
  agentUserId: number | null;
  agent: string | null;
  inboxId: number | null;
  inbox: string | null;
  channel: string | null;
  channelDisplayName: string | null;
  externalIdentityType: string | null;
  externalIdentityValue: string | null;
  platform: string | null;
  agentEmployeeId: number | null;
  channelType: string | null;
  channelAddress: string | null;
};
export type ContactOption = { id: number; name: string; kind: string; conversations: number };
export type ContactRow = { contact: ContactPerson; conversation: ContactSession | null };
export type ContactPageResult = {
  items: ContactRow[];
  total: number;
  page: number;
  pageSize: number;
  access: ContactRights;
  timezone: string;
  inboxes: ContactOption[];
  agents: ContactOption[];
  tags: string[];
  scopeRevision: string | null;
  hasUnassigned: boolean;
};
export type ContactDetailResult = {
  contact: ContactPerson;
  access: ContactRights;
  conversations: ContactSession[];
  timezone: string;
  nextBefore: number | null;
  scopeRevision: string | null;
};
export type ContactQuery = {
  search: string | null;
  tags: string[] | null;
  anyTag: boolean;
  inboxId: number | null;
  agentUserId: number | null;
  dateField: string;
  period: string;
  from: string | null;
  to: string | null;
  sort: string;
  page: number;
  pageSize: number;
  agentKind: string;
  selectedIds: number[] | null;
};
export type ContactCommercial = {
  email: string | null;
  phone: string | null;
  relationshipStatus: string | null;
  tags: string[];
};
export type ContactContext = {
  organizationId: number;
  userId: number;
  access: ContactRights | null;
  scopeRevision: string | null;
  sessionStamp: string | null;
};
export type ContactImportItem = {
  number: number;
  firstName: string;
  lastName: string;
  details: ContactCommercial | null;
  state: string;
  reason: string | null;
  contactId: number | null;
  operationId: string | null;
};
export type ContactImportTagResolution = {
  name: string;
  kind: string;
  rows: number[];
  target: TagDefinition | null;
  create: boolean;
};
export type ContactImportTagReceipt = { name: string; tagId: number; result: string; recordedAt: string };
export type ContactImportResult = {
  id: string;
  rows: ContactImportItem[];
  createdAt: string;
  result: ContactImportItem[] | null;
  totalRows: number;
  readyCount: number;
  savedCount: number;
  pendingCount: number;
  visibleTotal: number;
  page: number;
  rowVersion: number;
  tagResolutions: ContactImportTagResolution[] | null;
  tagResults: ContactImportTagReceipt[] | null;
};
export type ContactImportFileResult = {
  headers: string[];
  examples: string[];
  totalRows: number;
  suggestedMapping: Record<string, number>;
  detectedSeparator: string;
  columnExamples: string[][] | null;
};
export type ContactImportTagChoice = {
  name: string;
  create: boolean;
  targetId: number | null;
  expectedVersion: number | null;
};
export type { TagCatalogRow, TagDefinition };

/** Derechos con los valores por omisión del contrato de .NET (módulo y chat encendidos si no vienen). */
export function rights(value: Partial<ContactRights> | null | undefined): ContactRights {
  return {
    canRead: value?.canRead ?? false,
    canWrite: value?.canWrite ?? false,
    canReadAccounts: value?.canReadAccounts ?? false,
    canWriteAccounts: value?.canWriteAccounts ?? false,
    moduleEnabled: value?.moduleEnabled ?? true,
    globalScope: value?.globalScope ?? false,
    canImport: value?.canImport ?? false,
    canExport: value?.canExport ?? false,
    canOpenChat: value?.canOpenChat ?? true,
    canReadTags: value?.canReadTags ?? false,
    canManageTags: value?.canManageTags ?? false,
  };
}

/** Máscara de autoridad (Mask): un cambio en cualquiera de estos permisos se trata como cambio de alcance. */
export const mask = (value: ContactRights) =>
  [value.canRead, value.canWrite, value.globalScope, value.canImport, value.canExport, value.canOpenChat]
    .map((item) => (item ? "True" : "False"))
    .join(":");

export const emptyQuery: ContactQuery = {
  search: null,
  tags: null,
  anyTag: false,
  inboxId: null,
  agentUserId: null,
  dateField: "CREATED",
  period: "ANY",
  from: null,
  to: null,
  sort: "CREATED",
  page: 1,
  pageSize: 10,
  agentKind: "HUMAN",
  selectedIds: null,
};

/** C9-01 · D1: cómo abre Contactos (Creación del contacto · Últimos 7 días, más recientes primero). */
export const startQuery: ContactQuery = { ...emptyQuery, dateField: "CREATED", period: "7D", sort: "CREATED" };

/** Lote fijo del celular (C17): carga de 25 en 25 al bajar, sin depender del tamaño de página de escritorio. */
export const mobileBatchSize = 25;
export const batchSized = (query: ContactQuery, mobile: boolean): ContactQuery =>
  mobile ? { ...query, pageSize: mobileBatchSize } : query;

/** D1: lo que filtraba u ordenaba por la última conversación se lee como fecha de creación. */
const created = (value: string) =>
  value.startsWith("CONVERSATION") ? "CREATED" + value.slice("CONVERSATION".length) : value;
export const creationDated = (query: ContactQuery): ContactQuery => ({
  ...query,
  dateField: created(query.dateField),
  sort: created(query.sort),
});

/** «Limpiar filtros»: vuelve al inicio y conserva búsqueda, orden y tamaño de página. */
export const clearedQuery = (current: ContactQuery): ContactQuery => ({
  ...startQuery,
  search: current.search,
  sort: current.sort,
  pageSize: current.pageSize,
});

/** Igualdad de consultas por valor (la del record de .NET, con las mismas etiquetas). */
export const queryKey = (query: ContactQuery) => JSON.stringify(query);
export const sameQuery = (a: ContactQuery, b: ContactQuery) => queryKey(a) === queryKey(b);

/** Uri.EscapeDataString: todo salvo letras, dígitos y - _ . ~ */
export function escapeData(value: string): string {
  return encodeURIComponent(value).replace(/[!'()*]/g, (char) => "%" + char.charCodeAt(0).toString(16).toUpperCase());
}

/** Uri.UnescapeDataString: deja tal cual lo que no es una secuencia válida. */
export function unescapeData(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value.replace(/(%[0-9a-f]{2})+/gi, (sequence) => {
      try {
        return decodeURIComponent(sequence);
      } catch {
        return sequence;
      }
    });
  }
}

const integer = /^\s*[+-]?\d+\s*$/;
const isDate = (value: string | undefined) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return year >= 1 && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

/** Consulta desde la dirección (ContactViewState.Parse), con los valores de inicio cuando faltan. */
export function parseContactQuery(search: string): ContactQuery {
  const pairs = new Map<string, string[]>();
  for (const part of search.replace(/^\?/, "").split("&")) {
    if (!part) continue;
    const index = part.indexOf("=");
    const key = unescapeData(index < 0 ? part : part.slice(0, index));
    const value = index < 0 ? "" : unescapeData(part.slice(index + 1).replace(/\+/g, " "));
    pairs.set(key, [...(pairs.get(key) ?? []), value]);
  }
  const value = (key: string) => pairs.get(key)?.[0];
  const number = (key: string) => {
    const raw = value(key);
    if (raw === undefined || !integer.test(raw)) return null;
    const parsed = Number(raw.trim());
    return Number.isSafeInteger(parsed) ? parsed : null;
  };
  return {
    search: value("q") ?? null,
    tags: pairs.get("tag") ?? null,
    anyTag: value("any") === "1",
    inboxId: number("inbox"),
    agentUserId: number("agent"),
    dateField: created(value("date") ?? startQuery.dateField),
    period: value("period") ?? startQuery.period,
    from: isDate(value("from")) ? value("from")! : null,
    to: isDate(value("to")) ? value("to")! : null,
    sort: created(value("order") ?? startQuery.sort),
    page: number("p") ?? 1,
    pageSize: number("n") ?? 10,
    agentKind: value("agentKind") ?? "HUMAN",
    selectedIds: null,
  };
}

/** Dirección de la lista o de un contacto (ContactViewState.Url): mismos parámetros y en el mismo orden. */
export function contactUrl(
  query: ContactQuery,
  id: number | null = null,
  edit: string | null = null,
  returnTags: string | null = null,
): string {
  const parts: string[] = [];
  const add = (key: string, value: string | number | null | undefined) => {
    if (value !== null && value !== undefined) parts.push(`${key}=${escapeData(String(value))}`);
  };
  add("q", query.search);
  for (const tag of query.tags ?? []) add("tag", tag);
  add("any", query.anyTag ? "1" : null);
  add("inbox", query.inboxId);
  add("agent", query.agentUserId);
  add("agentKind", query.agentUserId !== null || query.agentKind === "NONE" ? query.agentKind : null);
  add("date", query.dateField);
  add("period", query.period);
  add("from", query.from);
  add("to", query.to);
  add("order", query.sort);
  add("p", query.page);
  add("n", query.pageSize);
  add("edit", edit);
  add("returnTags", returnTags);
  return `/portal/chat/contactos${id !== null ? `/${id}` : ""}?${parts.join("&")}`;
}

/** El regreso a Etiquetas (returnTags) solo si apunta a Etiquetas. */
export function tagReturnUrl(search: string): string | null {
  const value = search
    .replace(/^\?/, "")
    .split("&")
    .find((part) => part.startsWith("returnTags="));
  const decoded = value === undefined ? null : unescapeData(value.slice("returnTags=".length));
  return decoded?.startsWith("/portal/chat/configuracion/etiquetas?") ? decoded : null;
}

// ---- Orden (ContactSort) ----
export const sortField = (field: string) =>
  field === "Contact" ? "NAME" : field === "CreatedAt" ? "CREATED" : field.toUpperCase();
export const sortDescending = (sort: string) => sort.endsWith("_DESC") || sort === "CREATED" || sort === "CONVERSATION";
export const sortColumn = (sort: string) => sort.replace("_ASC", "").replace("_DESC", "");
export function toggleSort(sort: string, field: string): string {
  const column = sortField(field);
  const descending =
    sortColumn(sort) === column ? !sortDescending(sort) : column === "CREATED" || column === "CONVERSATION";
  return column + (descending ? "_DESC" : "_ASC");
}
export const isDateColumn = (column: string) => column === "CREATED" || column === "CONVERSATION";
export const normalSort = (sort: string) => sortColumn(sort) + (sortDescending(sort) ? "_DESC" : "_ASC");

// ---- Columnas (ContactText) ----
/** Columnas que se pueden elegir (KH11 · D1). */
export const contactFields = ["Phone", "Tags", "CreatedAt", "Agent", "Inbox", "Email", "RelationshipStatus", "Channel"];
export const writableFields = ["FirstName", "LastName", "Email", "Phone", "RelationshipStatus", "Tags"];
/** Columnas de la vista base: Bandeja solo cuando la empresa tiene más de una. */
export const automaticFields = (inboxCount: number) =>
  ["Phone", "Tags", "CreatedAt", "Agent", "Inbox", "Email"].filter((field) => field !== "Inbox" || inboxCount > 1);
/** Un grupo o selector lleva buscador solo con más de 6 opciones. */
export const searchThreshold = 6;
/** Una píldora de etiqueta en la fila de filtros. */
export const rowTagLimit = 1;
export const hasImportPages = (visibleTotal: number) => visibleTotal > 3;
export const sourceSubtitle = (raw: string, fieldLabel: string) =>
  raw.trim().localeCompare(fieldLabel.trim(), undefined, { sensitivity: "accent" }) === 0 ? null : raw;
export const importCountKey = (count: number, saved: boolean) =>
  saved ? (count === 1 ? "ADDED_ONE" : "ADDED_MANY") : count === 1 ? "ADD_ONE" : "ADD_MANY";

// ---- Presentación (ContactPresentation) ----
const asciiDigit = (char: string) => char >= "0" && char <= "9";

/** Teléfono con su prefijo y grupos de tres (C24); sin valor, «—». */
export function phone(value: string | null | undefined): string {
  if (!value?.trim()) return "—";
  const digits = [...value].filter(asciiDigit).join("");
  if (digits.length < 7) return value;
  if (digits.length === 11 && digits.startsWith("1"))
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  const country = digits.startsWith("1") ? 1 : digits.startsWith("51") ? 2 : Math.max(1, digits.length - 9);
  const rest = digits.slice(country);
  const groups: string[] = [];
  for (let index = 0; index < rest.length; index += 3) groups.push(rest.slice(index, index + 3));
  if (groups.length >= 2 && groups[groups.length - 1]!.length === 1) {
    groups[groups.length - 2] += groups[groups.length - 1]!;
    groups.pop();
  }
  return "+" + digits.slice(0, country) + " " + groups.join(" ");
}

/** El canal como lo entiende la empresa: «WhatsApp · +51 944 047 139» (KH11). */
export function channel(session: ContactSession | null | undefined): string {
  if (!session) return "—";
  const name =
    (session.channelType?.toUpperCase() === "WHATSAPP" ? "WhatsApp" : null) ??
    (session.channel?.trim() ? session.channel.trim() : null);
  const value = session.channelAddress?.trim();
  const number =
    value &&
    [...value].every((char) => asciiDigit(char) || "+ -()".includes(char)) &&
    [...value].filter(asciiDigit).length >= 7
      ? phone(value)
      : null;
  const parts = [name, number].filter((part): part is string => part !== null);
  return parts.length === 0 ? "—" : parts.join(" · ");
}

const weekday = new Map<string, Intl.DateTimeFormat>();
const clock = new Map<string, Intl.DateTimeFormat>();
function formatter(cache: Map<string, Intl.DateTimeFormat>, key: string, make: () => Intl.DateTimeFormat) {
  let value = cache.get(key);
  if (!value) cache.set(key, (value = make()));
  return value;
}

/** Fecha corta de .NET («d»): es-PE dd/MM/yyyy, en-US M/d/yyyy. */
export function shortDate(day: string, locale: string): string {
  const [year, month, date] = day.split("-");
  // es-PE en el navegador (ICU de .NET): «d/MM/yyyy», sin cero inicial en el día (9/10/2026).
  return locale === "en-US" ? `${Number(month)}/${Number(date)}/${year}` : `${Number(date)}/${month}/${year}`;
}

/** Fecha y hora cortas de .NET («g») del valor en el desfase con que viene: 9/10/2026 21:38 o 10/9/2026 9:38 PM. */
export function shortDateTime(iso: string, locale: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (!match) return iso;
  const hour = Number(match[2]);
  return locale === "en-US"
    ? `${shortDate(match[1]!, locale)} ${hour % 12 === 0 ? 12 : hour % 12}:${match[3]} ${hour < 12 ? "AM" : "PM"}`
    : `${shortDate(match[1]!, locale)} ${hour}:${match[3]}`;
}

/** «Hoy 9:05», «Ayer 18:20», «Martes 19:50» o «24/10/2026 9:05», en la zona de la empresa (ContactPresentation.Date). */
export function contactDate(
  value: string | null | undefined,
  now: Date,
  timezone: string,
  locale: string,
  today: string,
  yesterday: string,
): string {
  if (!value) return "—";
  const zone = isTimeZone(timezone) ? timezone : "UTC";
  const instant = new Date(value);
  const day = dayInZone(instant, zone);
  const days = Math.round((Date.parse(dayInZone(now, zone)) - Date.parse(day)) / 86_400_000);
  let prefix: string;
  if (days === 0) prefix = today;
  else if (days === 1) prefix = yesterday;
  else if (days >= 2 && days <= 6) {
    const name = formatter(
      weekday,
      `${locale}|${zone}`,
      () => new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: zone }),
    ).format(instant);
    prefix = name.charAt(0).toUpperCase() + name.slice(1);
  } else prefix = shortDate(day, locale);
  const parts = formatter(
    clock,
    zone,
    () => new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: zone }),
  ).formatToParts(instant);
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? "0");
  const minute = parts.find((part) => part.type === "minute")?.value ?? "00";
  return `${prefix} ${hour}:${minute}`;
}

/** La fila coincide con la búsqueda por nombre, teléfono o correo (Matches). */
export const matches = (row: ContactRow, term: string | null | undefined) =>
  [fullName(row.contact), row.contact.phone, row.contact.email].some((value) => contains(value, term));

export const fullName = (person: ContactPerson) => `${person.firstName} ${person.lastName}`.trim();

/** Colores y uso de las etiquetas del catálogo (todas las páginas): el color guardado, o neutra. */
export async function readAllTags(
  page: (number: number) => Promise<{ items: TagCatalogRow[]; page: number; pageSize: number; total: number }>,
) {
  const rows = new Map<number, TagCatalogRow>();
  for (let number = 1; ; number++) {
    const current = await page(number);
    for (const row of current.items) rows.set(row.tag.id, row);
    if (current.items.length === 0 || current.page < number || number * current.pageSize >= current.total)
      return [...rows.values()];
  }
}

/** Claves de la pantalla: ContactText, ContactC2Text, ContactE1Text y ContactC1DesignText del portal actual. */
export const contactTextPrefixes = ["C1_", "C2_", "E1_", "E3_"];

/**
 * Textos de etiquetas que ya no nombran el CRM (Contactos es parte base de Bheli, dueño 26-10-2026); solo del portal
 * React, el Blazor sigue con E1_SHARED y E1_ASSIGN_HELP. Se suman a las listas generadas de Contactos y Etiquetas.
 */
export const contactBaseTextKeys = ["E1_SHARED_BASE", "E1_ASSIGN_HELP_BASE"];
